import React, { useState } from 'react';
import { collection, deleteDoc, doc, getDocs, updateDoc, writeBatch, query, where } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useUser } from '../context/UserContext';

export default function DatabaseCleanup() {
  const { user } = useUser();
  const [isRunning, setIsRunning] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [results, setResults] = useState<any>(null);

  // Solo admins pueden acceder
  if (!user || !user.tipo?.includes('admin')) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">
          <h4>Acceso Denegado</h4>
          <p>Solo administradores pueden realizar limpieza de base de datos.</p>
        </div>
      </div>
    );
  }

  const addLog = (message: string) => {
    setLogs(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
    console.log(message);
  };

  const limpiarBaseDatos = async () => {
    if (!window.confirm('⚠️ ADVERTENCIA: Esta operación eliminará TODOS los datos existentes en Firebase. ¿Estás seguro?')) {
      return;
    }

    if (!window.confirm('🔥 CONFIRMACIÓN FINAL: ¿Confirmas que quieres LIMPIAR COMPLETAMENTE la base de datos? Esta acción NO SE PUEDE DESHACER.')) {
      return;
    }

    setIsRunning(true);
    setLogs([]);
    setResults(null);

    try {
      addLog('🚀 Iniciando limpieza completa de base de datos...');
      
      const collectionsToClean = [
        'admin', 'asociado', 'asesor', 'inmobiliaria', 
        'propiedades', 'proyectos', 'taller', 
        'inscripcion', 'calificacion', 'configuracion_rating'
      ];
      
      const results = {
        deleted: {} as { [key: string]: number },
        errors: [] as string[]
      };

      for (const collectionName of collectionsToClean) {
        try {
          addLog(`🧹 Limpiando colección: ${collectionName}...`);
          
          const snapshot = await getDocs(collection(db, collectionName));
          const docsToDelete = snapshot.docs;
          
          if (docsToDelete.length === 0) {
            addLog(`✅ ${collectionName}: Ya está vacía`);
            results.deleted[collectionName] = 0;
            continue;
          }

          // Eliminar en lotes de 500 (límite de Firestore)
          const batchSize = 500;
          let deletedCount = 0;
          
          for (let i = 0; i < docsToDelete.length; i += batchSize) {
            const batch = writeBatch(db);
            const batchDocs = docsToDelete.slice(i, i + batchSize);
            
            batchDocs.forEach(document => {
              batch.delete(document.ref);
            });
            
            await batch.commit();
            deletedCount += batchDocs.length;
            
            addLog(`📦 ${collectionName}: Eliminados ${deletedCount}/${docsToDelete.length} documentos`);
          }
          
          results.deleted[collectionName] = deletedCount;
          addLog(`✅ ${collectionName}: Limpieza completada (${deletedCount} docs eliminados)`);
          
        } catch (error: any) {
          const errorMsg = `❌ Error limpiando ${collectionName}: ${error.message}`;
          addLog(errorMsg);
          results.errors.push(errorMsg);
        }
      }

      addLog('🎉 Limpieza de base de datos completada!');
      setResults(results);
      
    } catch (error: any) {
      addLog(`💥 Error crítico en limpieza: ${error.message}`);
    } finally {
      setIsRunning(false);
    }
  };

  const limpiezaParcial = async (collectionsSelected: string[]) => {
    if (!window.confirm(`⚠️ ¿Eliminar datos solo de: ${collectionsSelected.join(', ')}?`)) {
      return;
    }

    setIsRunning(true);
    setLogs([]);

    try {
      addLog('🧹 Iniciando limpieza parcial...');
      
      for (const collectionName of collectionsSelected) {
        const snapshot = await getDocs(collection(db, collectionName));
        
        for (const document of snapshot.docs) {
          await deleteDoc(document.ref);
        }
        
        addLog(`✅ ${collectionName}: ${snapshot.size} documentos eliminados`);
      }
      
      addLog('✅ Limpieza parcial completada');
      
    } catch (error: any) {
      addLog(`❌ Error en limpieza parcial: ${error.message}`);
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-12">
          <div className="card border-danger">
            <div className="card-header bg-danger text-white">
              <h3 className="mb-0">🧹 Limpieza de Base de Datos</h3>
              <small>⚠️ Herramientas de limpieza para preparar migración AMPI</small>
            </div>
            <div className="card-body">
              
              {/* Advertencias */}
              <div className="alert alert-danger">
                <h5>🚨 ADVERTENCIA CRÍTICA</h5>
                <ul className="mb-0">
                  <li>Esta herramienta <strong>ELIMINA PERMANENTEMENTE</strong> los datos</li>
                  <li>No hay forma de recuperar los datos una vez eliminados</li>
                  <li>Asegúrate de tener un backup antes de proceder</li>
                  <li>Solo usar si estás 100% seguro</li>
                </ul>
              </div>

              {/* Opciones de limpieza */}
              <div className="row mb-4">
                <div className="col-md-6">
                  <div className="card border-warning">
                    <div className="card-header bg-warning text-dark">
                      <h5 className="mb-0">🔥 Limpieza Completa</h5>
                    </div>
                    <div className="card-body">
                      <p>Elimina <strong>TODOS</strong> los datos de <strong>TODAS</strong> las colecciones:</p>
                      <ul className="small">
                        <li>admin, asociado, asesor</li>
                        <li>inmobiliaria, propiedades, proyectos</li>
                        <li>taller, inscripcion, calificacion</li>
                        <li>configuracion_rating</li>
                      </ul>
                      <button
                        className="btn btn-danger w-100"
                        onClick={limpiarBaseDatos}
                        disabled={isRunning}
                      >
                        {isRunning ? '🔥 Limpiando...' : '🔥 LIMPIAR TODO'}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="card border-info">
                    <div className="card-header bg-info text-white">
                      <h5 className="mb-0">🎯 Limpieza Selectiva</h5>
                    </div>
                    <div className="card-body">
                      <p>Elimina solo las colecciones relacionadas con usuarios:</p>
                      <div className="d-grid gap-2">
                        <button
                          className="btn btn-outline-danger"
                          onClick={() => limpiezaParcial(['admin', 'asociado', 'asesor'])}
                          disabled={isRunning}
                        >
                          🗑️ Solo Usuarios (Todos)
                        </button>
                        
                        <div className="row">
                          <div className="col-6">
                            <button
                              className="btn btn-outline-secondary btn-sm w-100"
                              onClick={() => limpiezaParcial(['asociado'])}
                              disabled={isRunning}
                            >
                              👨‍💼 Solo Asociados
                            </button>
                          </div>
                          <div className="col-6">
                            <button
                              className="btn btn-outline-secondary btn-sm w-100"
                              onClick={() => limpiezaParcial(['asesor'])}
                              disabled={isRunning}
                            >
                              🏘️ Solo Asesores (Afiliados)
                            </button>
                          </div>
                        </div>
                        
                        <button
                          className="btn btn-outline-warning"
                          onClick={() => limpiezaParcial(['inmobiliaria'])}
                          disabled={isRunning}
                        >
                          🏢 Solo Inmobiliarias
                        </button>
                        <button
                          className="btn btn-outline-info"
                          onClick={() => limpiezaParcial(['inscripcion', 'calificacion'])}
                          disabled={isRunning}
                        >
                          📝 Solo Inscripciones/Calificaciones
                        </button>
                        <button
                          className="btn btn-outline-warning"
                          onClick={() => limpiezaParcial(['propiedades'])}
                          disabled={isRunning}
                        >
                          🏠 Solo Propiedades
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Logs */}
              {logs.length > 0 && (
                <div className="card">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">📋 Logs de Limpieza</h5>
                    <span className="badge bg-secondary">{logs.length}</span>
                  </div>
                  <div className="card-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {logs.map((log, index) => (
                      <div key={index} className="font-monospace small mb-1 p-1 border-start border-2 border-info">
                        {log}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Resultados */}
              {results && (
                <div className="card border-success mt-3">
                  <div className="card-header bg-success text-white">
                    <h5 className="mb-0">📊 Resultados de Limpieza</h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      {Object.entries(results.deleted).map(([collection, count]) => (
                        <div key={collection} className="col-md-3 mb-2">
                          <div className="card text-center">
                            <div className="card-body">
                              <h6>{collection}</h6>
                              <h4 className={(count as number) > 0 ? 'text-success' : 'text-muted'}>{count as number}</h4>
                              <small>eliminados</small>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {results.errors.length > 0 && (
                      <div className="alert alert-danger mt-3">
                        <h6>Errores durante limpieza:</h6>
                        <ul className="mb-0">
                          {results.errors.map((error: string, i: number) => (
                            <li key={i}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}